import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { releaseBasename } from './releasePrefix';

import { AppShell, PublicLayout, profilesForNavigationItem } from '@/components/layout';
import type { NavigationItemId } from '@/components/layout';

import { HomeRedirect } from './HomeRedirect';
import { NotFoundRoute } from './NotFoundRoute';
import { OrderPaymentRedirect } from './OrderPaymentRedirect';
import { RequireAuth } from './RequireAuth';
import { RequireProfile } from './RequireProfile';
import { RouteErrorBoundary } from './RouteErrorBoundary';

/**
 * Wraps a module route in the profile guard, reading the admitted profiles from the same mapping
 * that drives the sidebar — so a link that is hidden and a URL that is typed give the same answer
 * and neither can drift from the other.
 */
function guarded(id: NavigationItemId, route: RouteObject): RouteObject {
  return {
    element: <RequireProfile allow={profilesForNavigationItem(id)} />,
    errorElement: <RouteErrorBoundary />,
    children: [route],
  };
}

/**
 * Every feature screen is loaded through `lazy` so the initial bundle carries only the shell.
 * Splitting per route is what keeps the catalog inside the p95 < 800 ms budget (RNF04).
 */
export const routes: RouteObject[] = [
  {
    // Public screens: signing in and getting back in. The reset page has to stay out of
    // `RequireAuth` as well as out of the login's own "already signed in" redirect — the link in
    // the e-mail signs the user in on arrival, and the form still has to show.
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/login',
        lazy: async () => {
          const { LoginRoute } = await import('@/features/auth');
          return { Component: LoginRoute };
        },
      },
      {
        path: '/recuperar-senha',
        lazy: async () => {
          const { ForgotPasswordRoute } = await import('@/features/auth');
          return { Component: ForgotPasswordRoute };
        },
      },
      {
        path: '/redefinir-senha',
        lazy: async () => {
          const { ResetPasswordRoute } = await import('@/features/auth');
          return { Component: ResetPasswordRoute };
        },
      },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <HomeRedirect /> },
          guarded('catalog', {
            path: 'catalogo',
            children: [
              {
                index: true,
                lazy: async () => {
                  const { CatalogRoute } = await import('@/features/catalog');
                  return { Component: CatalogRoute };
                },
              },
              {
                // The SKU id, not the code: the code is editable in the backoffice and a link an
                // operator shared last week must keep pointing at the same product. `produto/`
                // keeps product pages apart from category pages (Figma D6).
                path: 'produto/:skuId',
                lazy: async () => {
                  const { SkuDetailRoute } = await import('@/features/catalog');
                  return { Component: SkuDetailRoute };
                },
              },
              {
                // A category's page (Figma D4) — or an old `/catalogo/:skuId` link, which the
                // route forwards to the product's new address.
                path: ':categoria',
                lazy: async () => {
                  const { CategoryRoute } = await import('@/features/catalog');
                  return { Component: CategoryRoute };
                },
              },
            ],
          }),
          guarded('cart', {
            path: 'carrinho',
            children: [
              {
                index: true,
                lazy: async () => {
                  const { CartRoute } = await import('@/features/cart');
                  return { Component: CartRoute };
                },
              },
              {
                // Review and payment are one screen now ("Finalizar", on the cart's own path); a
                // bookmarked review link lands there instead of on a 404.
                path: 'revisao',
                element: <Navigate to="/carrinho" replace />,
              },
            ],
          }),
          guarded('orders', {
            path: 'pedidos',
            children: [
              {
                index: true,
                lazy: async () => {
                  const { OrdersRoute } = await import('@/features/orders');
                  return { Component: OrdersRoute };
                },
              },
              {
                // Static before dynamic: react-router ranks the literal `historico` segment
                // above `:orderId`, so a history link can never be swallowed by the detail route.
                path: 'historico',
                lazy: async () => {
                  const { OrderHistoryRoute } = await import('@/features/orders');
                  return { Component: OrderHistoryRoute };
                },
              },
              {
                path: ':orderId',
                lazy: async () => {
                  const { OrderDetailRoute } = await import('@/features/orders');
                  return { Component: OrderDetailRoute };
                },
              },
              // The "pay now" screens lived here. Boleto is the only method now and it is issued
              // when the order closes (ADR-0025): an old link lands on the order, which shows it.
              { path: ':orderId/pagamento', element: <OrderPaymentRedirect /> },
              { path: ':orderId/pagamento/:paymentId', element: <OrderPaymentRedirect /> },
            ],
          }),
          guarded('payments', {
            path: 'pagamentos',
            lazy: async () => {
              const { PaymentsRoute } = await import('@/features/payments');
              return { Component: PaymentsRoute };
            },
          }),
          // Figma CL1: the bell's list as a page, for the profiles that buy (the bell's audience).
          guarded('cart', {
            path: 'notificacoes',
            lazy: async () => {
              const { NotificationsRoute } = await import('@/features/inactivity');
              return { Component: NotificationsRoute };
            },
          }),
          guarded('tracking', {
            path: 'rastreamento',
            lazy: async () => {
              const { TrackingRoute } = await import('@/features/tracking');
              return { Component: TrackingRoute };
            },
          }),
          // One guard for the whole administrative area rather than one per screen: the admitted
          // profiles are a property of the area, and repeating the list per child is how a new
          // screen silently ships unguarded.
          guarded('backoffice', {
            path: 'backoffice',
            lazy: async () => {
              const { BackofficeRoute } = await import('@/features/backoffice');
              return { Component: BackofficeRoute };
            },
            children: [
              {
                // Each profile lands on the section it opens first (`backofficeHomeForProfile`).
                index: true,
                lazy: async () => {
                  const { BackofficeHome } = await import('@/features/backoffice');
                  return { Component: BackofficeHome };
                },
              },
              {
                path: 'clientes',
                lazy: async () => {
                  const { CustomerListRoute } = await import('@/features/backoffice');
                  return { Component: CustomerListRoute };
                },
              },
              {
                path: 'clientes/novo',
                lazy: async () => {
                  const { CustomerFormRoute } = await import('@/features/backoffice');
                  return { Component: CustomerFormRoute };
                },
              },
              {
                // The customer's id is its tenant id; the tab lives in `?aba=`.
                path: 'clientes/:customerId',
                lazy: async () => {
                  const { CustomerDetailRoute } = await import('@/features/backoffice');
                  return { Component: CustomerDetailRoute };
                },
              },
              {
                // Unidades, Política and Histórico; the tab lives in `?aba=`.
                path: 'inatividade',
                lazy: async () => {
                  const { InactivityRoute } = await import('@/features/backoffice');
                  return { Component: InactivityRoute };
                },
              },
              {
                path: 'produtos',
                lazy: async () => {
                  const { SkuListRoute } = await import('@/features/backoffice');
                  return { Component: SkuListRoute };
                },
              },
              {
                path: 'produtos/novo',
                lazy: async () => {
                  const { SkuFormRoute } = await import('@/features/backoffice');
                  return { Component: SkuFormRoute };
                },
              },
              {
                path: 'produtos/importar',
                lazy: async () => {
                  const { SkuImportRoute } = await import('@/features/backoffice');
                  return { Component: SkuImportRoute };
                },
              },
              {
                path: 'produtos/:skuId/editar',
                lazy: async () => {
                  const { SkuFormRoute } = await import('@/features/backoffice');
                  return { Component: SkuFormRoute };
                },
              },
              {
                path: 'fornecedores',
                lazy: async () => {
                  const { SupplierListRoute } = await import('@/features/backoffice');
                  return { Component: SupplierListRoute };
                },
              },
              {
                path: 'unidades',
                lazy: async () => {
                  const { UnitOfMeasureListRoute } = await import('@/features/backoffice');
                  return { Component: UnitOfMeasureListRoute };
                },
              },
              {
                path: 'catalogos',
                lazy: async () => {
                  const { CatalogCurationRoute } = await import('@/features/backoffice');
                  return { Component: CatalogCurationRoute };
                },
              },
              {
                path: 'custos',
                lazy: async () => {
                  const { CostListRoute } = await import('@/features/backoffice');
                  return { Component: CostListRoute };
                },
              },
              {
                path: 'spreads',
                lazy: async () => {
                  const { SpreadRuleListRoute } = await import('@/features/backoffice');
                  return { Component: SpreadRuleListRoute };
                },
              },
              {
                path: 'tabelas-preco',
                lazy: async () => {
                  const { PriceTableListRoute } = await import('@/features/backoffice');
                  return { Component: PriceTableListRoute };
                },
              },
              {
                // The table id, not the name: the name is editable while the table is a draft.
                path: 'tabelas-preco/:priceTableId',
                lazy: async () => {
                  const { PriceTableDetailRoute } = await import('@/features/backoffice');
                  return { Component: PriceTableDetailRoute };
                },
              },
              {
                path: 'aprovacoes',
                lazy: async () => {
                  const { ApprovalsRoute } = await import('@/features/backoffice');
                  return { Component: ApprovalsRoute };
                },
              },
              {
                path: 'fechamento-diario',
                lazy: async () => {
                  const { DailySettlementsRoute } = await import('@/features/backoffice');
                  return { Component: DailySettlementsRoute };
                },
              },
              {
                path: 'exportacao',
                lazy: async () => {
                  const { ControllershipExportRoute } = await import('@/features/backoffice');
                  return { Component: ControllershipExportRoute };
                },
              },
              {
                path: 'conciliacao',
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { ReconciliationRoute } = await import('@/features/backoffice');
                      return { Component: ReconciliationRoute };
                    },
                  },
                  {
                    path: ':runId',
                    lazy: async () => {
                      const { ReconciliationRunRoute } = await import('@/features/backoffice');
                      return { Component: ReconciliationRunRoute };
                    },
                  },
                ],
              },
              {
                path: 'prazos',
                lazy: async () => {
                  const { LeadTimeRuleListRoute } = await import('@/features/backoffice');
                  return { Component: LeadTimeRuleListRoute };
                },
              },
              {
                path: 'fretes-cupons',
                lazy: async () => {
                  const { CheckoutSettingsRoute } = await import('@/features/backoffice');
                  return { Component: CheckoutSettingsRoute };
                },
              },
              {
                // Figma BC3/BC4: every customer's orders and one order with the status advance.
                path: 'pedidos',
                lazy: async () => {
                  const { BackofficeOrdersRoute } = await import('@/features/backoffice');
                  return { Component: BackofficeOrdersRoute };
                },
              },
              {
                path: 'pedidos/:orderId',
                lazy: async () => {
                  const { BackofficeOrderRoute } = await import('@/features/backoffice');
                  return { Component: BackofficeOrderRoute };
                },
              },
              {
                // Figma BC15/CT11: every customer's charges, read-only.
                path: 'pagamentos',
                lazy: async () => {
                  const { BackofficePaymentsRoute } = await import('@/features/backoffice');
                  return { Component: BackofficePaymentsRoute };
                },
              },
              {
                path: 'entregas',
                lazy: async () => {
                  const { DeliveryQueueRoute } = await import('@/features/backoffice');
                  return { Component: DeliveryQueueRoute };
                },
              },
              {
                path: 'contratos',
                lazy: async () => {
                  const { ContractListRoute } = await import('@/features/backoffice');
                  return { Component: ContractListRoute };
                },
              },
              {
                path: 'posicao-contratual',
                lazy: async () => {
                  const { ContractPositionPanelRoute } = await import('@/features/backoffice');
                  return { Component: ContractPositionPanelRoute };
                },
              },
              {
                path: 'titulos',
                lazy: async () => {
                  const { ReceivableListRoute } = await import('@/features/backoffice');
                  return { Component: ReceivableListRoute };
                },
              },
              {
                // The administrator's landing: what needs attention today, read off the panels
                // below. Every request it makes is gated by the API like the panels themselves.
                path: 'visao-geral',
                lazy: async () => {
                  const { AdminOverviewRoute } = await import('@/features/backoffice');
                  return { Component: AdminOverviewRoute };
                },
              },
              {
                // Sprint 12: the KPI panel (SPRINT12.md, item 1). Backend-guarded to
                // (CONTROLLERSHIP, PLATFORM_ADMIN); this route relies on that 403, the same
                // "hiding a link is a usability affordance, not the security control" rule
                // `ReconciliationRoute` already lives under.
                path: 'indicadores',
                lazy: async () => {
                  const { KpiPanelRoute } = await import('@/features/backoffice');
                  return { Component: KpiPanelRoute };
                },
              },
            ],
          }),
          // Sprint 8: the tenant's own contract, its period position and the risk banner
          // (SPRINT08.md, "Frontend", item 1). Guarded the same way `orders` is, since the term
          // checkout it explains is reachable from the same profiles as the order it pays for.
          guarded('contracts', {
            path: 'contratos',
            lazy: async () => {
              const { ContractRoute } = await import('@/features/contracts');
              return { Component: ContractRoute };
            },
          }),
          // Reachable by every profile on purpose: it is the screen that shows a user which
          // tenant they are inside, which is what makes the isolation demonstrable.
          guarded('organization', {
            path: 'minha-organizacao',
            lazy: async () => {
              const { OrganizationRoute } = await import('@/features/organization');
              return { Component: OrganizationRoute };
            },
          }),
          { path: '*', element: <NotFoundRoute /> },
        ],
      },
    ],
  },
];

// Resolved against the prefix this copy is published under, so the frozen `/vX.Y.Z/` copies
// route as well as the root does. Without it they served the shell and then answered 404 to
// every path inside themselves.
export const router = createBrowserRouter(routes, {
  basename: releaseBasename(),
});
